import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PhotoIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import AdminLayout from '../../components/AdminLayout';

interface Collection {
  _id: string;
  name: string;
  description: string;
  image: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const AdminCollections = () => {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCollection, setEditingCollection] = useState<Collection | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: '',
    isActive: true
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/collections/admin/all', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) setCollections(data.data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file: File) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Image uploaded');
        return data.data.url;
      } else {
        throw new Error(data.message);
      }
    } catch {
      toast.error('Failed to upload image');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    let imageUrl = formData.image;
    
    // Upload new image if selected
    if (imageFile) {
      const uploadedPath = await handleImageUpload(imageFile);
      if (!uploadedPath) return;
      imageUrl = uploadedPath;
    }

    const collectionData = {
      ...formData,
      image: imageUrl
    };

    try {
      const token = localStorage.getItem('adminToken');
      const url = editingCollection 
        ? `/api/collections/${editingCollection._id}`
        : '/api/collections';
      
      const method = editingCollection ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(collectionData)
      });

      const data = await response.json();
      
      if (data.success) {
        await fetchCollections();
        resetForm();
        toast.success(editingCollection ? 'Collection updated!' : 'Collection created!');
      } else {
        toast.error(data.message || 'Failed to save collection');
      }
    } catch {
      toast.error('Failed to save collection');
    }
  };

  const handleEdit = (collection: Collection) => {
    setEditingCollection(collection);
    setFormData({
      name: collection.name,
      description: collection.description,
      image: collection.image,
      isActive: collection.isActive
    });
    setShowForm(true);
  };

  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/collections/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        await fetchCollections();
        toast.success('Collection deleted');
      } else {
        toast.error(data.message || 'Failed to delete');
      }
    } catch {
      toast.error('Failed to delete collection');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const toggleStatus = async (collection: Collection) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/collections/${collection._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...collection,
          isActive: !collection.isActive
        })
      });

      const data = await response.json();
      
      if (data.success) {
        await fetchCollections();
        toast.success(`Collection ${collection.isActive ? 'hidden' : 'activated'}`);
      } else {
        toast.error(data.message || 'Failed to update status');
      }
    } catch {
      toast.error('Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      image: '',
      isActive: true
    });
    setImageFile(null);
    setEditingCollection(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <AdminLayout title="Collections">
        <div className="flex items-center justify-center py-32"><div className="spinner-lg" /></div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Collections"
      subtitle={`${collections.length} collection${collections.length !== 1 ? 's' : ''}`}
      actions={
        <button onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 bg-offblack text-offwhite px-4 py-2 text-sm font-medium tracking-wide hover:bg-charcoal transition-colors">
          <PlusIcon className="w-4 h-4" /> Add Collection
        </button>
      }
    >
      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {collections.map((collection) => (
          <div key={collection._id} className="bg-white border border-warmgray-200 overflow-hidden group">
            <div className="relative aspect-[16/9] overflow-hidden bg-warmgray-100">
              <img src={collection.image} alt={collection.name} className="w-full h-full object-cover" />
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => toggleStatus(collection)}
                  className="p-1.5 bg-white shadow-sm hover:bg-warmgray-100 transition-colors"
                  aria-label={collection.isActive ? 'Hide collection' : 'Show collection'}>
                  {collection.isActive ? <EyeIcon className="w-3.5 h-3.5 text-offblack" /> : <EyeSlashIcon className="w-3.5 h-3.5 text-warmgray-400" />}
                </button>
                <button onClick={() => handleEdit(collection)}
                  className="p-1.5 bg-white shadow-sm hover:bg-warmgray-100 transition-colors"
                  aria-label={`Edit ${collection.name}`}>
                  <PencilIcon className="w-3.5 h-3.5 text-offblack" />
                </button>
                <button onClick={() => setDeleteConfirm(collection._id)}
                  className="p-1.5 bg-white shadow-sm hover:bg-red-50 transition-colors"
                  aria-label={`Delete ${collection.name}`}>
                  <TrashIcon className="w-3.5 h-3.5 text-red-600" />
                </button>
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-medium text-offblack">{collection.name}</h3>
                <span className={`text-[0.65rem] tracking-wide uppercase px-2 py-0.5 ${collection.isActive ? 'bg-green-50 text-green-700' : 'bg-warmgray-100 text-warmgray-500'}`}>
                  {collection.isActive ? 'Active' : 'Hidden'}
                </span>
              </div>
              <p className="text-xs text-warmgray-500 line-clamp-2">{collection.description}</p>
              <p className="text-[0.6rem] text-warmgray-400 mt-2">Created {new Date(collection.createdAt).toLocaleDateString()}</p>
            </div>
          </div>
        ))}
      </div>

      {collections.length === 0 && (
        <div className="text-center py-16">
          <PhotoIcon className="w-16 h-16 mx-auto text-warmgray-300 mb-3" />
          <h3 className="text-lg font-display text-offblack mb-1">No collections yet</h3>
          <p className="text-sm text-warmgray-500 mb-6">Create your first collection to organize products.</p>
          <button onClick={() => setShowForm(true)} className="bg-offblack text-offwhite px-6 py-2.5 text-sm tracking-wider uppercase hover:bg-charcoal transition-colors">Add Collection</button>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white p-6 max-w-sm w-full mx-4">
            <h3 className="font-display text-lg text-offblack mb-2">Delete Collection</h3>
            <p className="text-sm text-warmgray-600 mb-6">Are you sure? This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 bg-red-600 text-white py-2 text-sm font-medium hover:bg-red-700 transition-colors">Delete</button>
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 border border-warmgray-200 py-2 text-sm font-medium text-offblack hover:bg-warmgray-50 transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white max-w-lg w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl text-offblack">{editingCollection ? 'Edit Collection' : 'New Collection'}</h2>
              <button onClick={resetForm} className="p-1 hover:bg-warmgray-100 transition-colors"><XMarkIcon className="w-5 h-5 text-warmgray-500" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="coll-name" className="block text-xs tracking-wider uppercase text-warmgray-500 mb-1.5">Name *</label>
                <input id="coll-name" type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack focus:outline-none focus:border-offblack transition-colors" placeholder="Collection name" />
              </div>
              <div>
                <label htmlFor="coll-desc" className="block text-xs tracking-wider uppercase text-warmgray-500 mb-1.5">Description *</label>
                <textarea id="coll-desc" required value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack h-20 resize-none focus:outline-none focus:border-offblack transition-colors" placeholder="Collection description" />
              </div>
              <div>
                <label className="block text-xs tracking-wider uppercase text-warmgray-500 mb-1.5">Image</label>
                {formData.image && <img src={formData.image} alt="Current" className="w-24 h-24 object-cover border border-warmgray-200 mb-3" />}
                <div className="border border-dashed border-warmgray-300 p-4">
                  <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files?.[0] || null)} className="hidden" id="coll-image-upload" />
                  <label htmlFor="coll-image-upload" className="cursor-pointer block text-center">
                    <PhotoIcon className="w-8 h-8 text-warmgray-400 mx-auto mb-2" />
                    <p className="text-sm text-warmgray-500">{imageFile ? imageFile.name : 'Click to upload'}</p>
                  </label>
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} className="w-4 h-4 accent-offblack" />
                <span className="text-sm text-offblack">Active</span>
              </label>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={uploading}
                  className="flex-1 bg-offblack text-offwhite py-2.5 text-sm font-medium tracking-wide hover:bg-charcoal transition-colors disabled:opacity-50">
                  {uploading ? 'Uploading...' : editingCollection ? 'Update Collection' : 'Create Collection'}
                </button>
                <button type="button" onClick={resetForm}
                  className="flex-1 border border-warmgray-200 py-2.5 text-sm font-medium text-offblack hover:bg-warmgray-50 transition-colors">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminCollections;
